//Frontend/src/admin/UsersManagement.js
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { FaPlus, FaEdit, FaTrash, FaUsers, FaTimes, FaSave, FaUserCheck, FaUserTimes, FaTimesCircle, FaEye, FaEyeSlash, FaShieldAlt, FaCheckCircle, FaEnvelope, FaLock, FaUser, FaIdCard, FaChevronLeft, FaChevronRight, FaUpload, FaImage, FaList } from 'react-icons/fa';
import axiosInstance from '../axios';
import '../styles/admin.css';
import '../styles/custom.css';

const PermissionSection = React.memo(({ category, categoryData, formPerms, onTogglePermission, onSelectAllInCategory }) => {
  const { icon, color, permissions } = categoryData;
  const allSelected = permissions.every(p => formPerms.includes(p.value));
  const someSelected = permissions.some(p => formPerms.includes(p.value)) && !allSelected;

  const getCategoryColorClass = (color) => {
    const colors = {
      blue: 'from-blue-500 to-blue-600',
      purple: 'from-purple-500 to-purple-600',
      green: 'from-green-500 to-green-600',
      pink: 'from-pink-500 to-pink-600',
      orange: 'from-orange-500 to-orange-600'
    };
    return colors[color] || colors.blue;
  };

  return (
    <div className="permission-card mb-4 p-4 rounded-xl bg-white border-2 border-gray-100 hover:border-gray-200 transition-all duration-300 hover:shadow-lg">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${getCategoryColorClass(color)} flex items-center justify-center text-white text-xl shadow-md`}>
            {icon}
          </div>
          <div>
            <h4 className="text-base font-bold text-gray-800">{category}</h4>
            <p className="text-xs text-gray-500">
              {permissions.filter(p => formPerms.includes(p.value)).length}/{permissions.length} сонгосон
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={() => onSelectAllInCategory(permissions)}
          className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all duration-200 ${
            allSelected 
              ? 'bg-gradient-to-r from-red-500 to-red-600 text-white hover:from-red-600 hover:to-red-700 shadow-md' 
              : someSelected
              ? 'bg-gradient-to-r from-yellow-400 to-yellow-500 text-white hover:from-yellow-500 hover:to-yellow-600 shadow-md'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          {allSelected ? '✕ Цуцлах' : someSelected ? '◐ Бүгд' : '✓ Бүгд'}
        </button>
      </div>
      <div className="space-y-2">
        {permissions.map((perm) => {
          const isChecked = formPerms.includes(perm.value);
          return (
            <div 
              key={perm.value} 
              className={`permission-item-enhanced flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-all duration-200 ${
                isChecked 
                  ? 'bg-gradient-to-r from-blue-50 to-indigo-50 border-2 border-blue-300 shadow-sm' 
                  : 'bg-gray-50 border-2 border-transparent hover:border-gray-200 hover:bg-white'
              }`}
              onClick={() => onTogglePermission(perm.value)}
            >
              <div className="relative">
                <input
                  type="checkbox"
                  checked={isChecked}
                  onChange={() => {}}
                  readOnly
                  className="custom-checkbox w-5 h-5 text-blue-600 border-2 border-gray-300 rounded focus:ring-2 focus:ring-blue-500 cursor-pointer pointer-events-none"
                />
                {isChecked && (
                  <FaCheckCircle className="absolute -top-1 -right-1 text-blue-600 text-xs animate-scale-in" />
                )}
              </div>
              <span className="text-lg">{perm.icon}</span>
              <span className={`text-sm flex-1 transition-all duration-200 ${
                isChecked ? 'text-blue-700 font-semibold' : 'text-gray-700 font-medium'
              }`}>
                {perm.label}
              </span>
              {isChecked && (
                <FaCheckCircle className="text-green-500 text-lg animate-scale-in" />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
});

const FormModal = React.memo(({ 
  isEdit, 
  formData, 
  formErrors, 
  showPassword, 
  submitLoading,
  uploadingImage,
  permissionCategories,
  onClose, 
  onSubmit, 
  onFormChange,
  onTogglePassword,
  onTogglePermission,
  onSelectAllInCategory,
  onSelectAllPermissions,
  onImageUpload,
  onRemoveImage
}) => {
  const allPermValues = useMemo(() => 
    Object.values(permissionCategories).flatMap(cat => cat.permissions.map(p => p.value)),
    [permissionCategories]
  );
  
  const allPermsSelected = useMemo(() => 
    allPermValues.every(p => formData.perms.includes(p)),
    [allPermValues, formData.perms]
  );

  const handleOverlayClick = useCallback((e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  }, [onClose]);

  const handleInputChange = useCallback((field, value) => {
    onFormChange(field, value);
  }, [onFormChange]);

  return (
    <div className="modal-overlay-enhanced animate-fadeIn" onClick={handleOverlayClick}>
      <div className="modal-content-enhanced max-w-7xl w-full mx-2 sm:mx-4 animate-slideUp" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header-enhanced bg-gradient-to-r from-blue-600 via-blue-700 to-indigo-700 text-white relative overflow-hidden">
          <div className="absolute inset-0 bg-grid-pattern opacity-10"></div>
          <div className="relative z-10 flex items-center justify-between">
            <div className="flex items-center gap-2 sm:gap-4">
              <div className="bg-white bg-opacity-20 p-2 sm:p-3 rounded-lg sm:rounded-xl backdrop-blur-sm">
                {isEdit ? <FaEdit className="w-4 h-4 sm:w-6 sm:h-6" /> : <FaPlus className="w-4 h-4 sm:w-6 sm:h-6" />}
              </div>
              <div>
                <h3 className="text-lg sm:text-2xl font-bold">
                  {isEdit ? 'Хэрэглэгч засах' : 'Шинэ хэрэглэгч бүртгэх'}
                </h3>
                <p className="text-blue-100 text-xs sm:text-sm mt-1 hidden sm:block">
                  {isEdit ? 'Хэрэглэгчийн мэдээлэл шинэчлэх' : 'Системд шинэ хэрэглэгч нэмэх'}
                </p>
              </div>
            </div>
            <button 
              type="button"
              onClick={onClose}
              className="text-white hover:bg-white hover:bg-opacity-20 rounded-lg sm:rounded-xl p-2 sm:p-3 transition-all duration-200 hover:rotate-90"
            >
              <FaTimes className="w-5 h-5 sm:w-6 sm:h-6" />
            </button>
          </div>
        </div>

        <div className="modal-body-enhanced max-h-[75vh] overflow-y-auto p-3 sm:p-6 bg-gradient-to-br from-gray-50 to-blue-50">
          <div className="grid grid-cols-1 xl:grid-cols-12 gap-3 sm:gap-6">
            <div className="xl:col-span-8 space-y-3 sm:space-y-6">
              <div className="bg-white p-3 sm:p-6 rounded-xl sm:rounded-2xl shadow-md border border-gray-100">
                <div className="flex items-center gap-2 sm:gap-3 mb-3 sm:mb-5 pb-3 sm:pb-4 border-b-2 border-blue-100">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-lg sm:rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white text-lg sm:text-xl shadow-lg">
                    <FaUser />
                  </div>
                  <div>
                    <h4 className="text-base sm:text-lg font-bold text-gray-800">Үндсэн мэдээлэл</h4>
                    <p className="text-xs text-gray-500 hidden sm:block">Хэрэглэгчийн мэдээлэл оруулах</p>
                  </div>
                </div>
                
                <div className="grid grid-cols-1 gap-3 sm:gap-5">
                  {/* Зураг оруулах */}
                  <div className="form-field col-span-2">
                    <label className="block text-sm font-medium mb-3">
                      <FaImage className="inline mr-2 text-blue-600" />
                      Профайл зураг
                    </label>
                    
                    {formData.image ? (
                      <div className="relative inline-block">
                        <img
                          src={`${window.location.origin}${formData.image}`}
                          alt="Profile"
                          className="w-32 h-32 object-cover rounded-full border-4 border-blue-100 shadow-lg"
                          onError={(e) => {
                            e.target.src = 'https://via.placeholder.com/128?text=No+Image';
                          }}
                        />
                        <button
                          type="button"
                          onClick={onRemoveImage}
                          className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-2 hover:bg-red-600 transition-all shadow-md"
                          title="Зураг устгах"
                        >
                          <FaTimes className="w-4 h-4" />
                        </button>
                      </div>
                    ) : (
                      <div className="flex items-center space-x-4">
                        <div className="w-32 h-32 bg-gray-100 rounded-full border-4 border-gray-200 flex items-center justify-center">
                          <FaUser className="text-gray-400 text-5xl" />
                        </div>
                        <div>
                          <label className="cursor-pointer">
                            <input
                              type="file"
                              accept="image/*"
                              onChange={onImageUpload}
                              className="hidden"
                              disabled={uploadingImage}
                            />
                            <span className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-all">
                              {uploadingImage ? (
                                <>
                                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2"></div>
                                  Хуулж байна...
                                </>
                              ) : (
                                <>
                                  <FaUpload className="mr-2" />
                                  Зураг сонгох
                                </>
                              )}
                            </span>
                          </label>
                          <p className="text-xs text-gray-500 mt-2">
                            JPG, PNG, GIF (Max 2MB)
                          </p>
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="form-field">
                    <label className="form-label">
                      <FaIdCard className="inline mr-2 text-blue-600" />
                      Хэрэглэгчийн нэр <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <input
                        type="text"
                        className={`form-input-enhanced ${formErrors.username ? 'border-red-400 bg-red-50' : ''}`}
                        placeholder="username"
                        value={formData.username}
                        onChange={(e) => handleInputChange('username', e.target.value)}
                      />
                    </div>
                    {formErrors.username && (
                      <p className="error-message animate-slideDown">
                        <FaTimesCircle className="mr-1" />
                        {formErrors.username}
                      </p>
                    )}
                  </div>

                  <div className="form-field">
                    <label className="form-label">
                      <FaUser className="inline mr-2 text-blue-600" />
                      Бүтэн нэр <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      className={`form-input-enhanced ${formErrors.fullname ? 'border-red-400 bg-red-50' : ''}`}
                      placeholder="Овог нэр"
                      value={formData.fullname}
                      onChange={(e) => handleInputChange('fullname', e.target.value)}
                    />
                    {formErrors.fullname && (
                      <p className="error-message animate-slideDown">
                        <FaTimesCircle className="mr-1" />
                        {formErrors.fullname}
                      </p>
                    )}
                  </div>

                  <div className="form-field">
                    <label className="form-label">
                      <FaEnvelope className="inline mr-2 text-blue-600" />
                      И-мэйл хаяг <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="email"
                      className={`form-input-enhanced ${formErrors.email ? 'border-red-400 bg-red-50' : ''}`}
                      placeholder="example@email.com"
                      value={formData.email}
                      onChange={(e) => handleInputChange('email', e.target.value)}
                    />
                    {formErrors.email && (
                      <p className="error-message animate-slideDown">
                        <FaTimesCircle className="mr-1" />
                        {formErrors.email}
                      </p>
                    )}
                  </div>

                  <div className="form-field">
                    <label className="form-label">
                      <FaLock className="inline mr-2 text-blue-600" />
                      {isEdit ? 'Шинэ нууц үг (хоосон бол өөрчлөхгүй)' : 'Нууц үг'} 
                      {!isEdit && <span className="text-red-500">*</span>}
                    </label>
                    <div className="relative">
                      <input
                        type={showPassword ? 'text' : 'password'}
                        className={`form-input-enhanced pr-12 ${formErrors.password ? 'border-red-400 bg-red-50' : ''}`}
                        placeholder="••••••••"
                        value={formData.password}
                        onChange={(e) => handleInputChange('password', e.target.value)}
                      />
                      <button
                        type="button"
                        onClick={onTogglePassword}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-all hover:scale-110"
                      >
                        {showPassword ? <FaEyeSlash className="w-5 h-5" /> : <FaEye className="w-5 h-5" />}
                      </button>
                    </div>
                    {formErrors.password && (
                      <p className="error-message animate-slideDown">
                        <FaTimesCircle className="mr-1" />
                        {formErrors.password}
                      </p>
                    )}
                  </div>

                  <div className="form-field">
                    <label className="form-label">
                      <FaShieldAlt className="inline mr-2 text-blue-600" />
                      Эрхийн түвшин
                    </label>
                    <select
                      className="form-select-enhanced"
                      value={formData.role}
                      onChange={(e) => handleInputChange('role', e.target.value)}
                    >
                      <option value="staff">👔 Ажилтан</option>
                      <option value="system_admin">🛡️ Системийн админ</option>
                    </select>
                  </div>

                  <div className="form-field flex items-center pt-6">
                    <label className="toggle-switch">
                      <input
                        type="checkbox"
                        checked={formData.is_active}
                        onChange={(e) => handleInputChange('is_active', e.target.checked)}
                      />
                      <span className="toggle-slider"></span>
                    </label>
                    <label className="ml-4 text-sm font-semibold text-gray-700 cursor-pointer">
                      Идэвхтэй байдал
                      <span className="block text-xs text-gray-500 font-normal mt-0.5">
                        {formData.is_active ? '✓ Идэвхтэй' : '✗ Идэвхгүй'}
                      </span>
                    </label>
                  </div>
                </div>
              </div>
            </div>

            <div className="xl:col-span-4">
              <div className="sticky top-4 bg-white rounded-2xl shadow-md border border-gray-100 overflow-hidden">
                <div className="bg-gradient-to-r from-indigo-600 to-purple-600 p-5 text-white">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-white bg-opacity-20 flex items-center justify-center backdrop-blur-sm">
                        <FaShieldAlt className="text-xl" />
                      </div>
                      <div>
                        <h4 className="text-lg font-bold">Эрхүүд</h4>
                        <p className="text-xs text-indigo-100">Сонгох эрхүүд</p>
                      </div>
                    </div>
                    <div className="bg-white bg-opacity-20 px-3 py-1.5 rounded-full backdrop-blur-sm">
                      <span className="text-sm font-bold">{formData.perms.length}/{allPermValues.length}</span>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={onSelectAllPermissions}
                    className={`w-full mt-3 px-4 py-2 rounded-lg font-semibold text-sm transition-all duration-200 ${
                      allPermsSelected
                        ? 'bg-white text-indigo-600 hover:bg-gray-100'
                        : 'bg-white bg-opacity-20 text-white hover:bg-opacity-30 border border-white border-opacity-30'
                    }`}
                  >
                    {allPermsSelected ? '✓ Бүгд сонгосон' : '☐ Бүгдийг сонгох'}
                  </button>
                </div>
                <div className="max-h-[580px] overflow-y-auto p-4 custom-scrollbar bg-gradient-to-b from-gray-50 to-white">
                  {Object.entries(permissionCategories).map(([category, categoryData]) => (
                    <PermissionSection 
                      key={category} 
                      category={category} 
                      categoryData={categoryData}
                      formPerms={formData.perms}
                      onTogglePermission={onTogglePermission}
                      onSelectAllInCategory={onSelectAllInCategory}
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="modal-footer-enhanced bg-gradient-to-r from-gray-50 to-blue-50 border-t-2 border-gray-200">
          <button 
            type="button"
            onClick={onClose}
            className="btn-secondary-enhanced"
            disabled={submitLoading}
          >
            <FaTimes className="mr-2" />
            Цуцлах
          </button>
          <button 
            type="button"
            onClick={onSubmit}
            className="btn-primary-enhanced"
            disabled={submitLoading}
          >
            {submitLoading ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent mr-2"></div>
                Хадгалж байна...
              </>
            ) : (
              <>
                <FaSave className="mr-2" />
                Хадгалах
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
});

const UsersManagement = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [formData, setFormData] = useState({
    username: '',
    fullname: '',
    email: '',
    password: '',
    role: 'staff',
    perms: [],
    is_active: true,
    image: ''
  });
  const [formErrors, setFormErrors] = useState({});
  const [deletingId, setDeletingId] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [showAll, setShowAll] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);

  const permissionCategories = useMemo(() => ({
    'Ачаа': {
      icon: '📦',
      color: 'blue',
      permissions: [
        { value: 'cargo:view', label: 'Ачаа харах', icon: '👁️' },
        { value: 'cargo:view-all', label: 'Бусад ачаа харах', icon: '🌐' },
        { value: 'cargo:create', label: 'Ачаа бүртгэх', icon: '➕' },
        { value: 'cargo:edit', label: 'Ачаа засах', icon: '✏️' },
        { value: 'cargo:delete', label: 'Ачаа устгах', icon: '🗑️' },
        { value: 'cargo_edit_price', label: 'Үнэ засах' },
      ]
    },
   'Чингэлэг': {
      icon: '📋',
      color: 'purple',
      permissions: [
        { value: 'container:view', label: 'Чингэлэг харах', icon: '👁️' },
        { value: 'container:create', label: 'Чингэлэг нэмэх', icon: '➕' },
        { value: 'container:edit', label: 'Чингэлэг засах', icon: '✏️' },
        { value: 'container:delete', label: 'Чингэлэг устгах', icon: '🗑️' },
      ]
    },
    'Төлбөр': {
      icon: '💰',
      color: 'green',
      permissions: [
        { value: 'payment:view', label: 'Төлбөр харах', icon: '👁️' },
        { value: 'payment:view-all', label: 'Бусад төлбөр харах', icon: '🌐' },
        { value: 'payment:edit', label: 'Төлбөр засах', icon: '✏️' },
        { value: 'payment:delete', label: 'Төлбөр устгах', icon: '🗑️' },
      ]
    },

    'Ангилал удирдлага': { // ✅ НЭМЭХ
    icon: <FaList />,
    color: 'orange',
    permissions: [
      { value: 'categories:view', label: 'Ангилал харах', icon: '👁️' },
      { value: 'categories:create', label: 'Ангилал үүсгэх', icon: '➕' },
      { value: 'categories:edit', label: 'Ангилал засах', icon: '✏️' },
      { value: 'categories:delete', label: 'Ангилал устгах', icon: '🗑️' }
    ]
  },

'Вэбсайт удирдлага': {
  icon: '🌐',
  color: 'teal',
  permissions: [
    { value: 'website:view', label: 'Вэбийн удирдлага харах', icon: '👁️' },
    
  ]
},

  

    'SMS': {
      icon: '📨',
      color: 'pink',
      permissions: [
        { value: 'sms:view', label: 'SMS харах', icon: '👁️' },
        { value: 'sms:send', label: 'SMS илгээх', icon: '📤' },
      ]
    },
    'Хэрэглэгч': {
      icon: '👥',
      color: 'orange',
      permissions: [
        { value: 'users:view', label: 'Хэрэглэгч харах', icon: '👁️' },
        { value: 'users:edit', label: 'Хэрэглэгч засах', icon: '✏️' },
        { value: 'users:delete', label: 'Хэрэглэгч устгах', icon: '🗑️' }
      ]
    },
    'Тохиргоо': {
      icon: '⚙️',
      color: 'blue',
      permissions: [
        { value: 'settings:view', label: 'Тохиргоо харах', icon: '👁️' },
        { value: 'settings:edit', label: 'Тохиргоо засах', icon: '✏️' }
      ]
    }
  }), []);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const response = await axiosInstance.get('/api/users');
      if (response.data.success) {
        setUsers(response.data.data);
      }
    } catch (error) {
      console.error('Users fetch error:', error);
      alert('Хэрэглэгчид татахад алдаа гарлаа');
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    if (!file.type.startsWith('image/')) {
      alert('Зөвхөн зураг файл сонгоно уу!');
      return;
    }
    
    if (file.size > 2 * 1024 * 1024) {
      alert('Зургийн хэмжээ 2MB-с бага байх ёстой!');
      return;
    }
    
    setUploadingImage(true);
    try {
      const uploadFormData = new FormData();
      uploadFormData.append('image', file);
      
      const response = await axiosInstance.post('/api/upload-image', uploadFormData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      if (response.data.success) {
        setFormData(prev => ({ ...prev, image: response.data.imageUrl }));
      }
    } catch (error) {
      console.error('Image upload error:', error);
      alert(error.response?.data?.msg || 'Зураг хуулахад алдаа гарлаа');
    } finally {
      setUploadingImage(false);
      e.target.value = '';
    }
  };

  const removeImage = async () => {
    if (!formData.image) return;
    
    try {
      await axiosInstance.delete('/api/delete-image', { 
        data: { imageUrl: formData.image } 
      });
    } catch (error) {
      console.error('Image delete error:', error);
    }
    
    setFormData(prev => ({ ...prev, image: '' }));
  };

  const resetForm = useCallback(() => {
    setFormData({
      username: '',
      fullname: '',
      email: '',
      password: '',
      role: 'staff',
      perms: [],
      is_active: true,
      image: ''
    });
    setFormErrors({});
    setShowPassword(false);
  }, []);

  const validateForm = useCallback((isEdit = false) => {
    const errors = {};
    
    if (!formData.username?.trim()) {
      errors.username = 'Хэрэглэгчийн нэр оруулна уу';
    } else if (formData.username.length < 3) {
      errors.username = 'Хэрэглэгчийн нэр 3-аас дээш тэмдэгт байх ёстой';
    }
    
    if (!formData.fullname?.trim()) {
      errors.fullname = 'Бүтэн нэр оруулна уу';
    }
    
    if (!formData.email?.trim()) {
      errors.email = 'И-мэйл оруулна уу';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = 'И-мэйл хаяг буруу байна';
    }
    
    if (!isEdit && !formData.password?.trim()) {
      errors.password = 'Нууц үг оруулна уу';
    } else if (formData.password && formData.password.length < 6) {
      errors.password = 'Нууц үг 6-аас дээш тэмдэгт байх ёстой';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  }, [formData]);

  const handleAdd = useCallback(async () => {
    if (!validateForm()) return;
    
    setSubmitLoading(true);
    try {
      const dataToSend = {
        ...formData,
        perms: formData.perms,
        image: formData.image || ''
      };
      
      const response = await axiosInstance.post('/api/users', dataToSend);
      
      if (response.data.success) {
        alert('✅ Хэрэглэгч амжилттай нэмэгдлээ');
        setShowAddModal(false);
        resetForm();
        fetchUsers();
      }
    } catch (error) {
      console.error('Add user error:', error);
      const errorMsg = error.response?.data?.msg || 'Хэрэглэгч нэмэхэд алдаа гарлаа';
      alert('❌ ' + errorMsg);
    } finally {
      setSubmitLoading(false);
    }
  }, [formData, validateForm, resetForm]);

  const handleEdit = useCallback(async () => {
    if (!validateForm(true)) return;
    
    setSubmitLoading(true);
    try {
      const dataToSend = {
        ...formData,
        perms: formData.perms,
        password: formData.password?.trim() || undefined,
        image: formData.image || ''
      };
      
      const response = await axiosInstance.put(`/api/users/${selectedUser.id}`, dataToSend);
      
      if (response.data.success) {
        alert('✅ Хэрэглэгч амжилттай шинэчлэгдлээ');
        setShowEditModal(false);
        resetForm();
        fetchUsers();
      }
    } catch (error) {
      console.error('Edit user error:', error);
      const errorMsg = error.response?.data?.msg || 'Хэрэглэгч засахад алдаа гарлаа';
      alert('❌ ' + errorMsg);
    } finally {
      setSubmitLoading(false);
    }
  }, [formData, selectedUser, validateForm, resetForm]);

  const handleDelete = useCallback(async (id) => {
    if (!window.confirm('⚠️ Устгахдаа итгэлтэй байна уу?')) return;
    
    setDeletingId(id);
    try {
      const response = await axiosInstance.delete(`/api/users/${id}`);
      if (response.data.success) {
        alert('✅ Хэрэглэгч амжилттай устгагдлаа');
        fetchUsers();
      }
    } catch (error) {
      console.error('Delete user error:', error);
      const errorMsg = error.response?.data?.msg || 'Хэрэглэгч устгахад алдаа гарлаа';
      alert('❌ ' + errorMsg);
    } finally {
      setDeletingId(null);
    }
  }, []);

  const openEditModal = useCallback((user) => {
    setSelectedUser(user);
    setFormData({
      username: user.username,
      fullname: user.fullname,
      email: user.email,
      password: '',
      role: user.role,
      perms: user.perms || [],
      is_active: user.is_active !== 0,
      image: user.image || ''
    });
    setShowEditModal(true);
  }, []);

  const handleFormChange = useCallback((field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  }, []);

  const togglePermission = useCallback((permValue) => {
    setFormData(prev => {
      const newPerms = prev.perms.includes(permValue)
        ? prev.perms.filter(p => p !== permValue)
        : [...prev.perms, permValue];
      return { ...prev, perms: newPerms };
    });
  }, []);

  const selectAllPermsInCategory = useCallback((permissions) => {
    const permValues = permissions.map(p => p.value);
    const allSelected = permValues.every(p => formData.perms.includes(p));
    
    setFormData(prev => {
      let newPerms = [...prev.perms];
      if (allSelected) {
        newPerms = newPerms.filter(p => !permValues.includes(p));
      } else {
        permValues.forEach(p => {
          if (!newPerms.includes(p)) {
            newPerms.push(p);
          }
        });
      }
      return { ...prev, perms: newPerms };
    });
  }, [formData.perms]);

  const selectAllPermissions = useCallback(() => {
    const allPermValues = Object.values(permissionCategories).flatMap(cat => 
      cat.permissions.map(p => p.value)
    );
    const allSelected = allPermValues.every(p => formData.perms.includes(p));
    
    setFormData(prev => ({
      ...prev,
      perms: allSelected ? [] : allPermValues
    }));
  }, [formData.perms, permissionCategories]);

  const handleCloseModal = useCallback(() => {
    setShowAddModal(false);
    setShowEditModal(false);
    resetForm();
  }, [resetForm]);

  const handleTogglePassword = useCallback(() => {
    setShowPassword(prev => !prev);
  }, []);

  const getRoleBadge = (role) => {
    const badges = {
      system_admin: 'bg-gradient-to-r from-red-500 to-red-600 text-white',
      staff: 'bg-gradient-to-r from-yellow-500 to-orange-500 text-white',
      user: 'bg-gradient-to-r from-blue-500 to-blue-600 text-white'
    };
    return badges[role] || badges.user;
  };

  const getRoleText = (role) => {
    const texts = {
      system_admin: 'Системийн админ',
      staff: 'Ажилтан',
      user: 'Хэрэглэгч'
    };
    return texts[role] || role;
  };

  const getPermissionLabel = (permValue) => {
    for (const category of Object.values(permissionCategories)) {
      const perm = category.permissions.find(p => p.value === permValue);
      if (perm) return perm.label;
    }
    return permValue;
  };

  const paginatedUsers = useMemo(() => {
    if (showAll) {
      return users;
    }
    const startIndex = (currentPage - 1) * itemsPerPage;
    return users.slice(startIndex, startIndex + itemsPerPage);
  }, [users, currentPage, itemsPerPage, showAll]);

  const totalPages = useMemo(() => {
    return Math.ceil(users.length / itemsPerPage);
  }, [users.length, itemsPerPage]);

  const handlePageChange = useCallback((newPage) => {
    setCurrentPage(newPage);
  }, []);

  const handleShowAllToggle = useCallback(() => {
    setShowAll(prev => !prev);
    setCurrentPage(1);
  }, []);

  return (
    <div className="users-management min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-4 md:p-6">
      <div className="mb-6">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-gray-800 mb-2 flex items-center">
              <div className="bg-gradient-to-r from-blue-600 to-blue-700 p-2 rounded-lg mr-3">
                <FaUsers className="text-white text-2xl" />
              </div>
              Хэрэглэгч удирдлага
            </h1>
            <p className="text-sm text-gray-600">Хэрэглэгч бүртгэх, эрх тохируулах</p>
          </div>
          <button
            onClick={() => {
              resetForm();
              setShowAddModal(true);
            }}
            className="px-6 py-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-lg hover:from-blue-700 hover:to-blue-800 transition-all font-medium flex items-center shadow-md hover:shadow-lg"
          >
            <FaPlus className="w-4 h-4 mr-2" />
            <span>Шинэ хэрэглэгч</span>
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-white p-5 rounded-lg shadow-md border-l-4 border-blue-500">
          <div className="flex justify-between items-center">
            <div>
              <p className="text-sm text-gray-500 mb-1">Нийт хэрэглэгч</p>
              <p className="text-2xl font-bold text-gray-800">{users.length}</p>
            </div>
            <div className="bg-blue-100 p-3 rounded-full">
              <FaUsers className="text-blue-600 text-xl" />
            </div>
          </div>
        </div>
        
        <div className="bg-white p-5 rounded-lg shadow-md border-l-4 border-green-500">
          <div className="flex justify-between items-center">
            <div>
              <p className="text-sm text-gray-500 mb-1">Идэвхтэй</p>
              <p className="text-2xl font-bold text-gray-800">
                {users.filter(u => u.is_active !== 0).length}
              </p>
            </div>
            <div className="bg-green-100 p-3 rounded-full">
              <FaUserCheck className="text-green-600 text-xl" />
            </div>
          </div>
        </div>
        
        <div className="bg-white p-5 rounded-lg shadow-md border-l-4 border-red-500">
          <div className="flex justify-between items-center">
            <div>
              <p className="text-sm text-gray-500 mb-1">Идэвхгүй</p>
              <p className="text-2xl font-bold text-gray-800">
                {users.filter(u => u.is_active === 0).length}
              </p>
            </div>
            <div className="bg-red-100 p-3 rounded-full">
              <FaUserTimes className="text-red-600 text-xl" />
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <div className="px-6 py-4 border-b bg-gradient-to-r from-gray-50 to-gray-100 flex justify-between items-center">
          <h2 className="text-lg font-semibold text-gray-800">Хэрэглэгчдийн жагсаалт</h2>
          <div className="flex items-center gap-4">
            <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
              <input
                type="checkbox"
                checked={showAll}
                onChange={handleShowAllToggle}
                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              />
              Бүгдийг харах
            </label>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b-2 border-gray-200">
              <tr>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  Зураг
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  Хэрэглэгч
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  Бүтэн нэр
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  И-мэйл
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  Эрхийн түвшин
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  Эрхүүд
                </th>
                <th className="px-6 py-4 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  Төлөв
                </th>
                <th className="px-6 py-4 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  Үйлдэл
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan="8" className="text-center py-12">
                    <div className="flex flex-col items-center">
                      <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-500 border-t-transparent mb-4"></div>
                      <p className="text-gray-500 font-medium">Ачааллаж байна...</p>
                    </div>
                  </td>
                </tr>
              ) : users.length === 0 ? (
                <tr>
                  <td colSpan="8" className="text-center py-12">
                    <FaUsers className="mx-auto text-5xl text-gray-300 mb-3" />
                    <p className="text-gray-500 font-medium">Хэрэглэгч байхгүй</p>
                  </td>
                </tr>
              ) : (
                paginatedUsers.map((user) => (
                  <tr key={user.id} className="hover:bg-blue-50 transition-colors">
                    <td className="px-6 py-4">
                      {user.image ? (
                        <img
                          src={`${window.location.origin}${user.image}`}
                          alt={user.username}
                          className="w-12 h-12 rounded-full object-cover border-2 border-blue-200 shadow-sm"
                          onError={(e) => {
                            // ✅ Зураг ачаалагдахгүй бол <img>-ийг нуух
                            e.target.style.display = 'none';
                            // ✅ Fallback div харуулах (дараагийн element)
                            if (e.target.nextElementSibling) {
                              e.target.nextElementSibling.style.display = 'flex';
                            }
                          }}
                        />
                      ) : null}
                      <div 
                        className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center text-white font-bold text-lg shadow-sm"
                        style={{ display: user.image ? 'none' : 'flex' }}
                      >
                        {user.username.charAt(0).toUpperCase()}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <p className="text-sm font-semibold text-gray-900">{user.username}</p>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-700">
                      {user.fullname}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600">
                      {user.email}
                    </td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex items-center px-3 py-1.5 rounded-full text-xs font-semibold ${getRoleBadge(user.role)}`}>
                        {getRoleText(user.role)}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      {user.perms && user.perms.length > 0 ? (
                        <div className="flex flex-wrap gap-1">
                          {user.perms.slice(0, 2).map((perm, idx) => (
                            <span key={idx} className="inline-flex items-center px-2 py-1 rounded bg-blue-100 text-blue-700 text-xs font-medium">
                              {getPermissionLabel(perm)}
                            </span>
                          ))}
                          {user.perms.length > 2 && (
                            <span className="inline-flex items-center px-2 py-1 rounded bg-gray-100 text-gray-600 text-xs font-semibold">
                              +{user.perms.length - 2}
                            </span>
                          )}
                        </div>
                      ) : (
                        <span className="text-gray-400 text-sm">-</span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-center">
                      {user.is_active !== 0 ? (
                        <span className="inline-flex items-center px-3 py-1.5 rounded-full bg-green-100 text-green-700 text-xs font-semibold">
                          <FaUserCheck className="mr-1.5" />
                          Идэвхтэй
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-3 py-1.5 rounded-full bg-gray-100 text-gray-600 text-xs font-semibold">
                          <FaUserTimes className="mr-1.5" />
                          Идэвхгүй
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex justify-center space-x-3">
                        <button
                          className="text-blue-600 hover:text-blue-800 hover:bg-blue-50 p-2 rounded-lg transition-all"
                          title="Засах"
                          onClick={() => openEditModal(user)}
                        >
                          <FaEdit className="w-4 h-4" />
                        </button>
                        <button
                          className="text-red-600 hover:text-red-800 hover:bg-red-50 p-2 rounded-lg transition-all disabled:opacity-40 disabled:cursor-not-allowed"
                          title="Устгах"
                          onClick={() => handleDelete(user.id)}
                          disabled={deletingId === user.id || user.role === 'system_admin'}
                        >
                          {deletingId === user.id ? (
                            <div className="animate-spin rounded-full h-4 w-4 border-2 border-red-600 border-t-transparent"></div>
                          ) : (
                            <FaTrash className="w-4 h-4" />
                          )}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        
        {!showAll && users.length > 0 && (
          <div className="px-6 py-4 border-t bg-gray-50 flex justify-between items-center">
            <div className="text-sm text-gray-600">
              Хуудас <span className="font-semibold">{currentPage}</span> / <span className="font-semibold">{totalPages}</span>
              <span className="mx-2">•</span>
              Нийт <span className="font-semibold">{users.length}</span> хэрэглэгч
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className="px-4 py-2 bg-white border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center gap-2"
              >
                <FaChevronLeft className="w-3 h-3" />
                Өмнөх
              </button>
              
              <div className="flex gap-1">
                {[...Array(totalPages)].map((_, index) => {
                  const page = index + 1;
                  if (
                    page === 1 ||
                    page === totalPages ||
                    (page >= currentPage - 1 && page <= currentPage + 1)
                  ) {
                    return (
                      <button
                        key={page}
                        onClick={() => handlePageChange(page)}
                        className={`px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                          currentPage === page
                            ? 'bg-blue-600 text-white'
                            : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
                        }`}
                      >
                        {page}
                      </button>
                    );
                  } else if (page === currentPage - 2 || page === currentPage + 2) {
                    return <span key={page} className="px-2 py-2 text-gray-500">...</span>;
                  }
                  return null;
                })}
              </div>

              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="px-4 py-2 bg-white border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center gap-2"
              >
                Дараах
                <FaChevronRight className="w-3 h-3" />
              </button>
            </div>
          </div>
        )}
      </div>

      {showAddModal && (
        <FormModal
          isEdit={false}
          formData={formData}
          formErrors={formErrors}
          showPassword={showPassword}
          submitLoading={submitLoading}
          uploadingImage={uploadingImage}
          permissionCategories={permissionCategories}
          onClose={handleCloseModal}
          onSubmit={handleAdd}
          onFormChange={handleFormChange}
          onTogglePassword={handleTogglePassword}
          onTogglePermission={togglePermission}
          onSelectAllInCategory={selectAllPermsInCategory}
          onSelectAllPermissions={selectAllPermissions}
          onImageUpload={handleImageUpload}
          onRemoveImage={removeImage}
        />
      )}

      {showEditModal && selectedUser && (
        <FormModal
          isEdit={true}
          formData={formData}
          formErrors={formErrors}
          showPassword={showPassword}
          submitLoading={submitLoading}
          uploadingImage={uploadingImage}
          permissionCategories={permissionCategories}
          onClose={handleCloseModal}
          onSubmit={handleEdit}
          onFormChange={handleFormChange}
          onTogglePassword={handleTogglePassword}
          onTogglePermission={togglePermission}
          onSelectAllInCategory={selectAllPermsInCategory}
          onSelectAllPermissions={selectAllPermissions}
          onImageUpload={handleImageUpload}
          onRemoveImage={removeImage}
        />
      )}
    </div>
  );
};

export default UsersManagement;